import { Handle, Position } from '@xyflow/react'
import { CantileverButton } from 'components/CantileverButton'
import { memo, type MouseEvent, useRef } from 'react'
import styled from 'styled-components'
import type { Port } from 'types'

const NodeContainer = styled.div<{
  width: number
  height: number
  buffer: number
}>`
  position: relative;
  width: ${(p) => p.width + 2 * p.buffer}px;
  height: ${(p) => p.height + 2 * p.buffer}px;
  overflow: visible;
  background: transparent;
  color: #fff;
  outline: none;
  border-radius: 0.25rem;
  cursor: crosshair;
`

const BufferLayer = styled.div<{
  width: number
  height: number
  buffer: number
}>`
  position: absolute;
  left: 0;
  top: 0;
  width: ${(p) => p.width + 2 * p.buffer}px;
  height: ${(p) => p.height + 2 * p.buffer}px;
  border: 1px dashed ${({ theme }) => theme.forecast.border2};
  border-radius: 0.25rem;
  pointer-events: none;
`

const ContentLayer = styled.div<{
  width: number
  height: number
  buffer: number
}>`
  position: absolute;
  left: ${(p) => p.buffer}px;
  top: ${(p) => p.buffer}px;
  width: ${(p) => p.width}px;
  height: ${(p) => p.height}px;
  cursor: crosshair;
  pointer-events: auto;
  /* Make it a positioning context for port wrappers */
  position: absolute;
  overflow: visible;

  svg {
    width: 100%;
    height: 100%;
    display: block;
    pointer-events: none;
  }
`

const PlacementOverlay = styled.div<{
  width: number
  height: number
  buffer: number
  disabled?: boolean
}>`
  position: absolute;
  left: 0;
  top: 0;
  width: ${(p) => p.width + 2 * p.buffer}px;
  height: ${(p) => p.height + 2 * p.buffer}px;
  cursor: crosshair;
  z-index: 2;
  background: transparent;
  pointer-events: ${(p) => (p.disabled ? 'none' : 'auto')};
`

export const SvgNode = memo(function SvgNode({
  data,
}: {
  data: {
    svg: string
    width: number
    height: number
    ports: Port[]
    removeMode?: boolean
    onRemovePort?: (id: string) => void
    onAddPort?: (xPct: number, yPct: number) => void
  }
}) {
  const buffer = 24
  const contentRef = useRef<HTMLDivElement>(null)
  const handlePlacementOverlayClick = (e: MouseEvent<HTMLDivElement>) => {
    if (!data.onAddPort || data.removeMode) return
    const outerRect = (
      e.currentTarget as HTMLDivElement
    ).getBoundingClientRect()
    const contentEl = contentRef.current
    const contentRect = contentEl
      ? contentEl.getBoundingClientRect()
      : {
          left: outerRect.left + buffer,
          top: outerRect.top + buffer,
          width: outerRect.width - 2 * buffer,
          height: outerRect.height - 2 * buffer,
        }
    const x = (e.clientX - contentRect.left) / contentRect.width
    const y = (e.clientY - contentRect.top) / contentRect.height
    // Allow buffer placements: x/y may be <0 or >1 here by up to buffer/content
    data.onAddPort(x, y)
    e.stopPropagation()
  }
  return (
    <NodeContainer width={data.width} height={data.height} buffer={buffer}>
      <BufferLayer width={data.width} height={data.height} buffer={buffer} />
      <ContentLayer
        ref={contentRef}
        width={data.width}
        height={data.height}
        buffer={buffer}>
        <div dangerouslySetInnerHTML={{ __html: data.svg }} />
        {data.ports.map((p) => (
          <div
            key={p.id}
            title={p.label}
            style={{
              position: 'absolute',
              left: `${p.x * 100}%`,
              top: `${p.y * 100}%`,
              transform: 'translate(-50%, -50%)',
              pointerEvents: 'auto',
            }}
            onContextMenu={(e) => {
              if (!data.removeMode) return
              e.preventDefault()
              data.onRemovePort?.(p.id)
            }}>
            {(p.type === 'source' || p.type === 'both') && (
              <Handle
                id={`${p.id}-s`}
                type='source'
                position={Position.Top}
                style={{ left: 0, top: 0, transform: 'translate(-50%, -50%)' }}
              />
            )}
            {(p.type === 'target' || p.type === 'both') && (
              <Handle
                id={`${p.id}-t`}
                type='source'
                position={Position.Top}
                style={{ left: 0, top: 0, transform: 'translate(-50%, -50%)' }}
              />
            )}
            {data.removeMode && data.onRemovePort && (
              <CantileverButton
                aria-label='Remove handle'
                title='Remove handle'
                danger
                iconOnly
                style={{
                  position: 'absolute',
                  right: -8,
                  top: -8,
                  transform: 'scale(0.5)',
                  transformOrigin: 'top right',
                }}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  data.onRemovePort?.(p.id)
                }}>
                ×
              </CantileverButton>
            )}
          </div>
        ))}
      </ContentLayer>
      <PlacementOverlay
        width={data.width}
        height={data.height}
        buffer={buffer}
        disabled={!!data.removeMode}
        onClick={handlePlacementOverlayClick}
      />
    </NodeContainer>
  )
})
